<template>
  <Teleport to="body">
    <Transition name="visible">
      <div v-if="isVisible" :class="['loading-files-widget', { 'is-collapced': isCollapced }]">
        <div class="loading-files-widget__header" @click="toggleCollapced">
          <span class="loading-files-widget__title">{{ title }}</span>
          <div class="loading-files-widget__controls">
            <div class="loading-files-widget__progress" v-if="isCollapced">
              <div class="loading-files-widget__progress-line">
                <div
                  class="loading-files-widget__progress-fill-line"
                  :style="{ width: `${generalProgress}%` }"
                />
              </div>
              <span class="loading-files-widget__progress-percent">{{ generalProgress }}%</span>
            </div>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
            >
              <template #leftSlot>
                <ChevronDownIcon class="loading-files-widget__chevron-icon" />
              </template>
            </TirPmButton>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              @click.stop="isVisible = false"
            >
              <template #leftSlot>
                <CrossCompactIcon />
              </template>
            </TirPmButton>
          </div>
        </div>
        <div
          class="loading-files-widget__main"
          :style="{ maxHeight: isCollapced ? '0' : dynamicHeight }"
        >
          <TirPmScrollbar :style="{ maxHeight: dynamicHeight }">
            <div class="loading-files-widget__files">
              <TirPmFilePreview
                v-for="file in files"
                :file="file"
                :variant="FilePreviewVariantEnum.Inline"
                :no-media-variant="FilePreviewVariantEnum.Inline"
                :key="file.FileId"
                @on-click-delete="FileManager.cancel({ FileId: file.FileId })"
              />
            </div>
          </TirPmScrollbar>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { FileManager, FileUploadStatusEnum } from "tir-pm-file-upload-manager";
import {
  FilePreviewAccessEnum,
  FilePreviewVariantEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmFilePreview,
  TirPmScrollbar,
} from "tir-components";
import { ChevronDownIcon, CrossCompactIcon } from "tir-style-system/icons/outline";
import { LoadingFileModel } from "./models";

/**Список загружаемых файлов */
const files = computed(() => {
  return FileManager.ActiveUploads.value.reduce((acc, file) => {
    if (
      (file.isFileModel() || file.isVideoModel()) &&
      file.Status === FileUploadStatusEnum.Uploading &&
      file.Progress !== undefined
    ) {
      return [
        ...acc,
        new LoadingFileModel({
          FileId: file.FileId,
          Url: file.Url,
          Name: file.Name,
          Size: file.Size,
          Progress: file.Progress,
          Accesses: file.IsSendingLastChunk ? [] : [FilePreviewAccessEnum.Remove],
        }),
      ];
    }
    return acc;
  }, new Array<LoadingFileModel>());
});

const calculateTotalProgressByFileSize = (files: LoadingFileModel[]): number => {
  let totalWeightedProgress = 0;
  let totalSize = 0;

  files.forEach((file) => {
    totalWeightedProgress += file.Progress * file.Size;
    totalSize += file.Size;
  });

  // Защита от деления на ноль, если размеры файлов равны нулю
  if (totalSize === 0) return 0;

  // Вычисляем общий прогресс как взвешенное среднее
  return totalWeightedProgress / totalSize;
};

const generalProgress = computed(() => calculateTotalProgressByFileSize(files.value).toFixed(0));

const fileHeight = 34 / 16; // 34px = высота файла + отступ, переведено в rem
const dynamicHeight = computed(() => {
  return `${Math.min((files.value.length + 20) * fileHeight + 0.875, 9)}rem`;
});

const title = computed(() => `Загружается файлов: ${files.value.length}`);

/**Видимо */
const isVisible = ref(false);

/**Свернуто */
const isCollapced = ref(true);
/**Переключиить флаг isCollapced */
const toggleCollapced = () => (isCollapced.value = !isCollapced.value);
watch(
  () => isVisible.value,
  () => {
    if (!isVisible.value) isCollapced.value = true;
  },
);
/** Следим за изменением списка файлов */
watch(
  () => files.value.length,
  (newFileCount, oldFileCount) => {
    if (!newFileCount) {
      isVisible.value = false;
      isCollapced.value = false;
    }
    // Если количество файлов увеличилось, значит добавлен новый файл
    else if (newFileCount > oldFileCount) {
      isVisible.value = true;
    }
  },
);
</script>

<style lang="scss" scoped>
.loading-files-widget {
  $self: &;

  position: fixed;
  z-index: 4999;
  right: 2.25rem;
  bottom: 0;
  padding: 0.75rem 0 0.75rem 1rem;

  width: 25.188rem;
  border-radius: var(--8, 0.5rem) var(--8, 0.5rem) 0 0;
  border: 0.063rem solid var(--text-qunternery, #bfc0c3);
  border-bottom: none;
  background-color: var(--bg-base, #fff);
  box-shadow:
    0px 0.25rem 0.5rem 0px rgba(0, 0, 0, 0.04),
    0px 0px 0.063rem 0px rgba(0, 0, 0, 0.04);

  transition: padding 0.2s ease-in-out;

  &__chevron-icon {
    transition: transform 0.2s ease-in-out;
  }

  &.is-collapced {
    padding-bottom: 0;
    padding-top: 0;

    #{$self}__chevron-icon {
      transform: rotate(180deg);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    cursor: pointer;
  }
  &__title {
    color: var(--text-secondary, #686c73);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.25rem;
    white-space: nowrap;
  }
  &__main {
    transition: max-height 0.2s ease-in-out;
  }
  &__files {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    margin: 0.75rem 1rem 0 0;
  }
  &__controls {
    display: flex;
    align-items: center;
  }
  &__progress {
    width: 5.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  &__progress-line {
    position: relative;
    overflow: hidden;
    flex: 1;
    flex-shrink: 0;
    height: 0.125rem;
    border-radius: 0.125rem;
    background-color: var(--bg-secondary, #f8f6f9);
  }
  &__progress-fill-line {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 0.125rem;
    background-color: var(--text-secondary, #686c73);
  }
  &__progress-percent {
    color: var(--text-secondary, #686c73);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1rem;
    white-space: nowrap;
  }
}

.visible-enter-active,
.visible-leave-active {
  transition: transform 0.2s ease-in-out;
}

.visible-enter-from,
.visible-leave-to {
  transform: translateY(100%);
}
</style>
<style lang="scss">
.loading-files-widget {
  &__files {
    .tir-pm-file-preview.is-inline.is-loading {
      max-width: 100% !important;
    }
  }
}
</style>
